import * as THREE from 'three';
import {DMSStringToDecimal} from './DSMD.js'

// Interpolate dip or azimuth at a given depth based on survey measurements
// Handles any number of survey points (1, 2, 3, or more)
const interpolateValue = (depth, surveyData, field, collarValue) => {
  if (!surveyData || surveyData.length === 0) {
    return null;
  }

  // Special case: only 1 survey point
  if (surveyData.length === 1) {
    const singlePoint = surveyData[0];
    // If depth is before the survey point, interpolate from collar to survey point
    if (depth < singlePoint.depth) {
      if (singlePoint.depth === 0) {
        return singlePoint[field];
      }
      // Interpolate between collar value (at depth 0) and survey point
      const t = depth / singlePoint.depth;
      return collarValue + t * (singlePoint[field] - collarValue);
    }
    // If depth is at or after the survey point, use the survey point value
    return singlePoint[field];
  }

  // Multiple survey points (2 or more)
  // If depth is before first survey point, interpolate from collar (depth 0) to first survey point
  if (depth < surveyData[0].depth) {
    if (surveyData[0].depth === 0) {
      return surveyData[0][field];
    }
    // Interpolate between collar value (at depth 0) and first survey point
    const t = depth / surveyData[0].depth;
    return collarValue + t * (surveyData[0][field] - collarValue);
  }

  // If depth is at or after last survey point, use last survey point
  if (depth >= surveyData[surveyData.length - 1].depth) {
    return surveyData[surveyData.length - 1][field];
  }

  // Find the two survey points to interpolate between (only runs if surveyData.length >= 2)
  for (let i = 0; i < surveyData.length - 1; i++) {
    if (depth >= surveyData[i].depth && depth <= surveyData[i + 1].depth) {
      const d1 = surveyData[i].depth;
      const d2 = surveyData[i + 1].depth;
      const v1 = surveyData[i][field];
      const v2 = surveyData[i + 1][field];
      
      // Handle edge case where depths are equal (shouldn't happen but be safe)
      if (d2 === d1) {
        return v1;
      }
      
      // Linear interpolation
      const t = (depth - d1) / (d2 - d1);
      return v1 + t * (v2 - v1);
    }
  }

  return null;
};

const calculateEndCoordinates = (start, direction, inclination, depth) => {
  //direction = DMSStringToDecimal(direction)+180
  //inclination = DMSStringToDecimal(inclination)
  //console.log(start, direction, inclination, depth)
  direction -= 180
  const dirRad = (parseFloat(direction) * Math.PI) / 180;
  const incRad = (parseFloat(inclination) * Math.PI) / 180;

  // const dx = depth * Math.cos(incRad) * Math.sin(dirRad);
  // const dy = depth * Math.cos(incRad) * Math.cos(dirRad);
  // const dz = depth * Math.sin(incRad);

  const dx = depth * Math.abs(Math.cos(incRad)) * Math.sin(dirRad);
  const dy = depth * Math.sin(incRad)
  const dz = depth * Math.abs(Math.cos(incRad)) * Math.cos(dirRad);

  const coords = {
    x: start.x - dx,
    y: start.y + dy,
    z: start.z + dz,
  };

 // console.log(start)
 // console.log(coords)
  return coords
};

// Calculate curved path using survey records as waypoints
// Each survey record defines a point calculated from the previous point
// Returns an object with points array and depths array (corresponding depths for each point)
const calculateCurvedPath = (start, collarDip, collarAzimuth, maxDepth, surveyData) => {
  const points = [start];
  const depths = [0]; // Track depth at each point
  let currentPosition = { ...start };
  let cumulativeDepth = 0;

  if (!surveyData || surveyData.length === 0) {
    // No survey data, use straight line
    const end = calculateEndCoordinates(start, collarAzimuth, collarDip, maxDepth);
    return { points: [start, end], depths: [0, maxDepth] };
  }

  // Process each survey record as a waypoint
  for (let i = 0; i < surveyData.length; i++) {
    const surveyRecord = surveyData[i];
    const surveyDepth = surveyRecord.depth;
    const surveyDip = surveyRecord.dip;
    const surveyAzimuth = surveyRecord.azimuth;

    // Special case: if first survey record depth is 0, use collar coordinates directly
    if (i === 0 && surveyDepth === 0) {
      // Point 0 (collar) is already added, use its coordinates for this survey point
      // Since depth is 0, this point is at the collar position
      currentPosition = { ...start };
      cumulativeDepth = 0;
      
      // If there's only one survey point at depth 0 and maxDepth > 0, extend from collar
      if (surveyData.length === 1 && maxDepth > 0) {
        const endPoint = calculateEndCoordinates(currentPosition, surveyAzimuth, surveyDip, maxDepth);
        points.push(endPoint);
        depths.push(maxDepth);
        return { points, depths };
      }
      continue;
    }

    // Calculate depth difference from previous position
    const depthDifference = surveyDepth - cumulativeDepth;
    
    // Skip if depth difference is negative or zero (shouldn't happen with sorted data)
    if (depthDifference <= 0) {
      continue;
    }

    // Calculate next point from current position using survey record's dip and azimuth
    const nextPoint = calculateEndCoordinates(
      currentPosition,
      surveyAzimuth,
      surveyDip,
      depthDifference
    );

    points.push(nextPoint);
    depths.push(surveyDepth);
    currentPosition = nextPoint;
    cumulativeDepth = surveyDepth;
  }

  // If hole extends beyond the last survey point, extend using last survey record's dip/azimuth
  if (cumulativeDepth < maxDepth && surveyData.length > 0) {
    const lastSurveyRecord = surveyData[surveyData.length - 1];
    const remainingDepth = maxDepth - cumulativeDepth;
    
    // Only extend if last survey point depth is not 0 (if it's 0, we already handled it above)
    if (lastSurveyRecord.depth > 0 || surveyData.length > 1) {
      const endPoint = calculateEndCoordinates(
        currentPosition,
        lastSurveyRecord.azimuth,
        lastSurveyRecord.dip,
        remainingDepth
      );
      points.push(endPoint);
      depths.push(maxDepth);
    }
  }

  return { points, depths };
};

export const transformData = ({ holes, assays, surveys = {} }, holeColumns, assayColumns , constants) => {
  const combinedData = {};

  Object.keys(holes).forEach((holeId) => {
    const hole = holes[holeId];
    const start = {
      x: parseFloat(hole[holeColumns['X']]) - constants[0],
      y: parseFloat(hole[holeColumns['Z']]) - constants[1],
      z: -parseFloat(hole[holeColumns['Y']]) + constants[2],
    };

    //console.log(start)

    const collarDip = parseFloat(hole[holeColumns['Dip']]);
    const collarAzimuth = parseFloat(hole[holeColumns['Azimuth']]);
    const maxDepth = parseFloat(hole[holeColumns['Depth']]);

    // Get survey data for this hole if available
    const surveyData = surveys[holeId] || null;

    // Generate curved path if survey data exists, otherwise use straight line
    let pathResult;
    if (surveyData && surveyData.length > 0) {
      pathResult = calculateCurvedPath(start, collarDip, collarAzimuth, maxDepth, surveyData);
    } else {
      // Fall back to straight line using collar values
      const end = calculateEndCoordinates(
        start,
        collarAzimuth,
        collarDip,
        maxDepth,
      );
      pathResult = { points: [start, end], depths: [0, maxDepth] };
    }
    const points = pathResult.points;
    const depths = pathResult.depths;

    // Helper function to find point along curved path at a specific depth
    const findPointAtDepth = (pathPoints, pathDepths, targetDepth) => {
      if (pathPoints.length === 2) {
        // Straight line case
        const t = targetDepth / maxDepth;
        return {
          x: pathPoints[0].x + t * (pathPoints[1].x - pathPoints[0].x),
          y: pathPoints[0].y + t * (pathPoints[1].y - pathPoints[0].y),
          z: pathPoints[0].z + t * (pathPoints[1].z - pathPoints[0].z),
        };
      }

      // Find which segment contains the target depth
      for (let i = 0; i < pathDepths.length - 1; i++) {
        const d1 = pathDepths[i];
        const d2 = pathDepths[i + 1];
        
        if (targetDepth >= d1 && targetDepth <= d2) {
          // Interpolate between points i and i+1 based on depth ratio
          const t = (targetDepth - d1) / (d2 - d1);
          return {
            x: pathPoints[i].x + t * (pathPoints[i + 1].x - pathPoints[i].x),
            y: pathPoints[i].y + t * (pathPoints[i + 1].y - pathPoints[i].y),
            z: pathPoints[i].z + t * (pathPoints[i + 1].z - pathPoints[i].z),
          };
        }
      }

      // If target depth is beyond last point, return last point
      return pathPoints[pathPoints.length - 1];
    };

    const holeAssays = assays[holeId] || [];
    const transformedAssays = holeAssays
      .filter((assay) => parseFloat(assay[assayColumns['grade']]) >= 0.5)
      .map((assay) => {
      const fromDepth = parseFloat(assay[assayColumns['from']]);
      const toDepth = parseFloat(assay[assayColumns['to']]);

      const fromPoint = findPointAtDepth(points, depths, fromDepth);
      const toPoint = findPointAtDepth(points, depths, toDepth);

      const assayLength = new THREE.Vector3(
        toPoint.x - fromPoint.x,
        toPoint.y - fromPoint.y,
        toPoint.z - fromPoint.z
      ).length();

      const assayPosition = new THREE.Vector3(
        (fromPoint.x + toPoint.x) / 2,
        (fromPoint.y + toPoint.y) / 2,
        (fromPoint.z + toPoint.z) / 2
      );

      // First rotate capsule 90 degrees around X-axis to align Z-axis with Y-axis
      const initialRotation = new THREE.Euler(Math.PI / 2, 0, 0);
      
      // Then align with the assay direction
      const directionRotation = new THREE.Euler().setFromRotationMatrix(
        new THREE.Matrix4().lookAt(
          new THREE.Vector3(fromPoint.x, fromPoint.y, fromPoint.z),
          new THREE.Vector3(toPoint.x, toPoint.y, toPoint.z),
          new THREE.Vector3(0, 1, 0)
        )
      );
      
      // Combine rotations
      const initialMatrix = new THREE.Matrix4().makeRotationFromEuler(initialRotation);
      const directionMatrix = new THREE.Matrix4().makeRotationFromEuler(directionRotation);
      const combinedMatrix = directionMatrix.multiply(initialMatrix);
      const assayRotation = new THREE.Euler().setFromRotationMatrix(combinedMatrix);

      const assayScale = new THREE.Vector3(1, assayLength-0.05, 1);

      const assayMatrix = new THREE.Matrix4()
        .makeTranslation(assayPosition.x, assayPosition.y, assayPosition.z)
        .multiply(new THREE.Matrix4().makeRotationFromEuler(assayRotation))
        .multiply(new THREE.Matrix4().makeScale(assayScale.x, assayScale.y, assayScale.z));

      const assayValue = parseFloat(assay[assayColumns['grade']]);
      let color;
      // if (assayValue < 1) color = 'green';
      // else if (assayValue < 3) color = 'yellow';
      // else if (assayValue >= 3) color = 'yellow';
      // else color = 'gray';

      
        if (assayValue >= 5) {
            color = 0x800080; // PURPLE
        } else if (assayValue >= 2.5) {
            color = 0xb45f06; // BROWN
        } else if (assayValue >= 1.5) {
            color = 0xcc0000; // RED
        } else if (assayValue >= 0.75) {
            color = 0x38761d; // GREEN
        } else {
            color = 'white'; // YELLOW
        }
        

      return {
        matrix: assayMatrix,
        color: new THREE.Color(color),
        data: assay,
      };
    });

    // Calculate holeMatrix from first and last points (for backward compatibility)
    // Note: For curved paths, we'll render using Line component instead of this matrix
    const lastPoint = points[points.length - 1];
    const holeMatrix = (() => {
      const lineLength = new THREE.Vector3(lastPoint.x - start.x, lastPoint.y - start.y, lastPoint.z - start.z).length();
      const position = new THREE.Vector3((start.x + lastPoint.x) / 2, (start.y + lastPoint.y) / 2, (start.z + lastPoint.z) / 2);
      const rotation = new THREE.Euler().setFromRotationMatrix(
        new THREE.Matrix4().lookAt(
          new THREE.Vector3(start.x, start.y, start.z),
          new THREE.Vector3(lastPoint.x, lastPoint.y, lastPoint.z),
          new THREE.Vector3(0, 1, 0)
        )
      );
      const scale = new THREE.Vector3(1, 1, lineLength);
      return new THREE.Matrix4()
        .makeTranslation(position.x, position.y, position.z)
        .multiply(new THREE.Matrix4().makeRotationFromEuler(rotation))
        .multiply(new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z));
    })();

    combinedData[holeId] = {
      holeId,
      holeMatrix,
      points,
      assays: transformedAssays,
      data: hole,
    };
  });

  return combinedData;
};
